import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from '../authConfig';

/**
 * Returns an async `getAccessToken()` for components that need a bearer token
 * before dispatching an authenticated thunk (e.g. creating/renaming an org).
 * Mirrors the acquireTokenSilent/acquireTokenRedirect fallback already used by
 * `App.jsx`'s `TokenAcquirer`, so that logic isn't re-implemented per feature.
 */
export function useApiAccessToken() {
    const { instance } = useMsal();

    const getAccessToken = useCallback(async ({ redirectOnInteractionRequired = true } = {}) => {
        const account = instance.getActiveAccount();
        if (!account) {
            throw new Error('No active account — user is not signed in.');
        }

        const request = { ...apiRequest, account };
        try {
            const response = await instance.acquireTokenSilent(request);
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError && redirectOnInteractionRequired) {
                await instance.acquireTokenRedirect(request);
                // acquireTokenRedirect navigates away; nothing left to return.
                return null;
            }
            throw error;
        }
    }, [instance]);

    return { getAccessToken };
}
